/**
 * CommentList Component
 * Displays a list of comment bubbles for the current page.
 */
import React, { useEffect, useRef, useCallback } from 'react';
import type { Comment } from '../../types';
import CommentBubble from './CommentBubble';
import ReviewedComment from './ReviewedComment';

interface CommentListProps {
  comments: Comment[];
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onEdit: (id: string) => void;
  onDismissCategory?: (category: string) => void;
  selectedCommentId?: string;
  onSelectComment?: (id: string) => void;
}

const CommentList: React.FC<CommentListProps> = ({
  comments,
  onAccept,
  onReject,
  onEdit,
  onDismissCategory,
  selectedCommentId,
  onSelectComment,
}) => {
  // Store refs to comment elements for scroll-into-view
  const commentRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Scroll selected comment into view when selection changes
  useEffect(() => {
    if (selectedCommentId) {
      const element = commentRefs.current.get(selectedCommentId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [selectedCommentId]);

  // Store ref for a comment
  const setCommentRef = useCallback(
    (id: string) => (el: HTMLDivElement | null) => {
      if (el) {
        commentRefs.current.set(id, el);
      } else {
        commentRefs.current.delete(id);
      }
    },
    []
  );

  if (comments.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">
        <p className="text-sm">No comments yet.</p>
        <p className="text-xs mt-1">Click "Generate Feedback" to analyze the document.</p>
      </div>
    );
  }

  const pendingComments = comments.filter((c) => c.status === 'pending');
  const reviewedComments = comments.filter((c) => c.status !== 'pending');

  return (
    <div className="space-y-4 p-4">
      {/* Pending comments */}
      {pendingComments.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Pending Review ({pendingComments.length})
          </h3>
          <div className="space-y-3">
            {pendingComments.map((comment) => (
              <div
                key={comment.id}
                ref={setCommentRef(comment.id)}
                data-comment-id={comment.id}
                onClick={() => onSelectComment?.(comment.id)}
                className="cursor-pointer"
              >
                <CommentBubble
                  comment={comment}
                  onAccept={onAccept}
                  onReject={onReject}
                  onEdit={onEdit}
                  onDismissCategory={onDismissCategory}
                  isSelected={selectedCommentId === comment.id}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reviewed comments */}
      {reviewedComments.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-2">
            Reviewed ({reviewedComments.length})
          </h3>
          <div className="space-y-2">
            {reviewedComments.map((comment) => (
              <div
                key={comment.id}
                ref={setCommentRef(comment.id)}
                data-comment-id={comment.id}
                onClick={() => onSelectComment?.(comment.id)}
                className={`cursor-pointer rounded ${
                  selectedCommentId === comment.id ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                <ReviewedComment comment={comment} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentList;
